import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Markets.css';

// TypeScript interfaces
interface MarketPair {
  id: string;
  symbol: string;
  pair: string;
  quoteSymbol: string;
  volume: string;
  price: string;
  change: string;
  changeType: 'positive' | 'negative';
  iconColor: string;
  icon: string;
}

// Mock data for Top Markets
const topMarketsData: MarketPair[] = [
  {
    id: 'btc-usdt',
    symbol: 'BTC',
    pair: 'BTC/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $45.2B',
    price: '$100,619.38',
    change: '+15.68%',
    changeType: 'positive',
    iconColor: '#f7931a',
    icon: '₿'
  },
  {
    id: 'eth-usdt',
    symbol: 'ETH',
    pair: 'ETH/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $28.5B',
    price: '$3,319.00',
    change: '+12.34%',
    changeType: 'positive',
    iconColor: '#627eea',
    icon: 'Ξ'
  },
  {
    id: 'sol-usdt',
    symbol: 'SOL',
    pair: 'SOL/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $12.8B',
    price: '$154.37',
    change: '+18.42%',
    changeType: 'positive',
    iconColor: '#14f195',
    icon: '◎'
  },
  {
    id: 'xrp-usdt',
    symbol: 'XRP',
    pair: 'XRP/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $8.4B',
    price: '$2.18',
    change: '+24.75%',
    changeType: 'positive',
    iconColor: '#23292f',
    icon: '✕'
  },
  {
    id: 'eth-btc',
    symbol: 'ETH',
    pair: 'ETH/BTC',
    quoteSymbol: 'BTC',
    volume: 'Vol $5.2B',
    price: '0.03298',
    change: '+8.91%',
    changeType: 'positive',
    iconColor: '#627eea',
    icon: 'Ξ'
  }
];

// Mock data for Top Gainers (sorted by highest change)
const topGainersData: MarketPair[] = [
  {
    id: 'xrp-usdt',
    symbol: 'XRP',
    pair: 'XRP/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $8.4B',
    price: '$2.18',
    change: '+24.75%',
    changeType: 'positive',
    iconColor: '#23292f',
    icon: '✕'
  },
  {
    id: 'sol-usdt',
    symbol: 'SOL',
    pair: 'SOL/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $12.8B',
    price: '$154.37',
    change: '+18.42%',
    changeType: 'positive',
    iconColor: '#14f195',
    icon: '◎'
  },
  {
    id: 'btc-usdt',
    symbol: 'BTC',
    pair: 'BTC/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $45.2B',
    price: '$100,619.38',
    change: '+15.68%',
    changeType: 'positive',
    iconColor: '#f7931a',
    icon: '₿'
  },
  {
    id: 'eth-usdt',
    symbol: 'ETH',
    pair: 'ETH/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $28.5B',
    price: '$3,319.00',
    change: '+12.34%',
    changeType: 'positive',
    iconColor: '#627eea',
    icon: 'Ξ'
  },
  {
    id: 'eth-btc',
    symbol: 'ETH',
    pair: 'ETH/BTC',
    quoteSymbol: 'BTC',
    volume: 'Vol $5.2B',
    price: '0.03298',
    change: '+8.91%',
    changeType: 'positive',
    iconColor: '#627eea',
    icon: 'Ξ'
  }
];

// Mock data for Top Losers
const topLosersData: MarketPair[] = [
  {
    id: 'doge-usdt',
    symbol: 'DOGE',
    pair: 'DOGE/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $2.1B',
    price: '$0.3842',
    change: '-8.45%',
    changeType: 'negative',
    iconColor: '#c2a633',
    icon: 'Ð'
  },
  {
    id: 'ada-usdt',
    symbol: 'ADA',
    pair: 'ADA/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $1.8B',
    price: '$0.9123',
    change: '-5.67%',
    changeType: 'negative',
    iconColor: '#0033ad',
    icon: '₳'
  },
  {
    id: 'bnb-usdt',
    symbol: 'BNB',
    pair: 'BNB/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $3.2B',
    price: '$645.32',
    change: '-3.21%',
    changeType: 'negative',
    iconColor: '#f3ba2f',
    icon: '◆'
  },
  {
    id: 'link-usdt',
    symbol: 'LINK',
    pair: 'LINK/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $890M',
    price: '$14.87',
    change: '-2.89%',
    changeType: 'negative',
    iconColor: '#2a5ada',
    icon: '⬡'
  },
  {
    id: 'dot-usdt',
    symbol: 'DOT',
    pair: 'DOT/USDT',
    quoteSymbol: 'USDT',
    volume: 'Vol $654M',
    price: '$7.23',
    change: '-1.54%',
    changeType: 'negative',
    iconColor: '#e6007a',
    icon: '●'
  }
];

const Markets: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'markets' | 'gainers' | 'losers'>('markets');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeNav, setActiveNav] = useState<string>('markets');
  const [tradeModalOpen, setTradeModalOpen] = useState(false);

  // Get data based on active tab
  const getActiveData = (): MarketPair[] => {
    switch (activeTab) {
      case 'gainers':
        return topGainersData;
      case 'losers':
        return topLosersData;
      default:
        return topMarketsData;
    }
  };

  // Filter data based on search query
  const filteredData = getActiveData().filter((item) =>
    item.pair.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.symbol.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get quote icon based on symbol
  const getQuoteIcon = (quoteSymbol: string) => {
    switch (quoteSymbol) {
      case 'USDT':
        return '₮';
      case 'BTC':
        return '₿';
      default:
        return '₮';
    }
  };

  // Get quote icon color
  const getQuoteIconColor = (quoteSymbol: string) => {
    switch (quoteSymbol) {
      case 'USDT':
        return 'var(--color-success)';
      case 'BTC':
        return '#f7931a';
      default:
        return 'var(--color-success)';
    }
  };

  return (
    <div className="markets-container">
      {/* Search Bar */}
      <div className="markets-search-wrapper">
        <div className="markets-search">
          <svg className="markets-search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            type="text"
            className="markets-search-input"
            placeholder="Search Pair"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Content */}
      <div className="markets-content">
        {/* Page Title */}
        <h1 className="markets-title">Market Scope</h1>

        {/* Filter Tabs */}
        <div className="markets-tabs">
          <button
            className={`markets-tab ${activeTab === 'markets' ? 'active' : ''}`}
            onClick={() => setActiveTab('markets')}
          >
            Top Markets
          </button>
          <button
            className={`markets-tab ${activeTab === 'gainers' ? 'active' : ''}`}
            onClick={() => setActiveTab('gainers')}
          >
            Top Gainers
          </button>
          <button
            className={`markets-tab ${activeTab === 'losers' ? 'active' : ''}`}
            onClick={() => setActiveTab('losers')}
          >
            Top Losers
          </button>
        </div>

        {/* Market List */}
        <div className="markets-list">
          {filteredData.map((market) => (
            <button key={market.id} className="markets-item">
              <div className="markets-icons">
                <div className="markets-coin-icon markets-coin-icon-primary" style={{ backgroundColor: market.iconColor }}>
                  {market.icon}
                </div>
                <div className="markets-coin-icon markets-coin-icon-quote" style={{ backgroundColor: getQuoteIconColor(market.quoteSymbol) }}>
                  {getQuoteIcon(market.quoteSymbol)}
                </div>
              </div>
              <div className="markets-info">
                <div className="markets-pair-name">{market.pair}</div>
                <div className="markets-volume">{market.volume}</div>
              </div>
              <div className="markets-price-col">
                <div className="markets-price">{market.price}</div>
                <div className={`markets-change ${market.changeType}`}>
                  {market.change}
                </div>
              </div>
              <svg className="markets-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          ))}

          {/* Empty state when no results */}
          {filteredData.length === 0 && (
            <div className="markets-empty">
              <p>No trading pairs found for "{searchQuery}"</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="markets-nav">
        <button
          className={`nav-item ${activeNav === 'home' ? 'active' : ''}`}
          onClick={() => navigate('/dashboard')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Home</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'markets' ? 'active' : ''}`}
          onClick={() => setActiveNav('markets')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 12L18 16L14 12L10 16L6 12L2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 6L18 10L14 6L10 10L6 6L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Markets</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'trade' ? 'active' : ''}`}
          onClick={() => {
            setActiveNav('trade');
            setTradeModalOpen(true);
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Trade</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'history' ? 'active' : ''}`}
          onClick={() => navigate('/history-deposit')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>History</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'assets' ? 'active' : ''}`}
          onClick={() => navigate('/assets')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Assets</span>
        </button>
      </nav>

      {/* Trade Modal */}
      {tradeModalOpen && (
        <div className="trade-modal-overlay" onClick={() => setTradeModalOpen(false)}>
          <div className="trade-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <button
              className="trade-modal-close"
              onClick={() => setTradeModalOpen(false)}
              aria-label="Close modal"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button className="trade-modal-option" onClick={() => navigate('/p2p-buy-list')}>
              <div className="trade-modal-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="17" cy="17" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="trade-modal-option-content">
                <h3 className="trade-modal-option-title">P2P</h3>
                <p className="trade-modal-option-description">
                  Unlock financial freedom through P2P crypto trading: connect directly, trade efficiently, and decentralize your assets.
                </p>
              </div>
              <svg className="trade-modal-option-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button className="trade-modal-option">
              <div className="trade-modal-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.707 15.293C4.077 15.923 4.523 17 5.414 17H17M17 17C15.895 17 15 17.895 15 19C15 20.105 15.895 21 17 21C18.105 21 19 20.105 19 19C19 17.895 18.105 17 17 17ZM9 19C9 20.105 8.105 21 7 21C5.895 21 5 20.105 5 19C5 17.895 5.895 17 7 17C8.105 17 9 17.895 9 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="trade-modal-option-content">
                <h3 className="trade-modal-option-title">Buy Crypto</h3>
                <p className="trade-modal-option-description">
                  Maximize crypto gains: Buy low, Sell high. Navigate digital markets with precision for optimal profits.
                </p>
              </div>
              <svg className="trade-modal-option-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Markets;
